// src/lib/theme.js
// Theme controller for the app.
// Uses: <html class="dark"> + CSS variables in src/styles/tailwind.css
//
// Preference values stored in localStorage["theme"]:
//   - "light"
//   - "dark"
//   - "system" (default if missing)
//
// The DOM gets:
//   - <html class="dark"> when resolved theme is dark
//   - <html data-theme="dark|light"> always set to resolved theme

export const THEME_STORAGE_KEY = "theme"; // "light" | "dark" | "system"

let _syncStarted = false;
let _mql = null;
const _subscribers = new Set();

function _safeGetLS() {
  try {
    return window?.localStorage;
  } catch {
    return null;
  }
}

export function getSystemTheme() {
  if (typeof window === "undefined") return "light";
  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
}

export function getThemePreference() {
  const ls = _safeGetLS();
  const raw = ls?.getItem(THEME_STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "system") return raw;
  return "system";
}

export function resolveTheme(preference) {
  const pref = preference || "system";
  return pref === "system" ? getSystemTheme() : pref;
}

export function getResolvedTheme() {
  return resolveTheme(getThemePreference());
}

function applyResolvedTheme(resolved) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.setAttribute("data-theme", resolved);
}

function emit() {
  const state = {
    preference: getThemePreference(),
    resolved: getResolvedTheme(),
  };
  _subscribers.forEach((cb) => {
    try {
      cb(state);
    } catch {
      // ignore subscriber errors
    }
  });
}

export function subscribeTheme(cb) {
  _subscribers.add(cb);
  // fire immediately
  try {
    cb({ preference: getThemePreference(), resolved: getResolvedTheme() });
  } catch {}
  return () => _subscribers.delete(cb);
}

export function setThemePreference(preference) {
  const pref =
    preference === "light" || preference === "dark" || preference === "system"
      ? preference
      : "system";

  const ls = _safeGetLS();
  try {
    if (ls) ls.setItem(THEME_STORAGE_KEY, pref);
  } catch {}

  // Ensure sync is active (needed for "system")
  startSystemThemeSync();

  const resolved = resolveTheme(pref);
  applyResolvedTheme(resolved);
  emit();
  return { preference: pref, resolved };
}

/**
 * Quick toggle used from header: toggles between explicit light/dark,
 * based on current resolved theme (even if preference is "system").
 */
export function toggleThemePreference() {
  const resolved = getResolvedTheme();
  const nextPref = resolved === "dark" ? "light" : "dark";
  return setThemePreference(nextPref);
}

export function initTheme() {
  // Apply current resolved theme ASAP.
  applyResolvedTheme(getResolvedTheme());
  startSystemThemeSync();
  emit();
}

export function startSystemThemeSync() {
  if (_syncStarted || typeof window === "undefined") return;
  _syncStarted = true;

  try {
    _mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!_mql) return;

    const handler = () => {
      // Only react to system changes when preference is "system"
      if (getThemePreference() !== "system") return;
      const resolved = getSystemTheme();
      applyResolvedTheme(resolved);
      emit();
    };

    // Modern browsers
    if (typeof _mql.addEventListener === "function") {
      _mql.addEventListener("change", handler);
    } else if (typeof _mql.addListener === "function") {
      // Safari / older
      _mql.addListener(handler);
    }
  } catch {
    // ignore
  }
}
